import { find, map, reduce } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ordersMonitoringApi } from 'api';
import {
  DataWrapper,
  PageHeader,
  QueryFilters,
  QueryColumns,
} from 'components';
import { DefaultPaymentTypeCode, FilterDefinitionType, QueryKey } from 'enums';
import { useCurrencies, usePartialQuery, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  PaymentType,
  PayoutOrdersMonitoringFilters,
} from 'types';
import { orderMonitoringUtils } from 'utils';

import { PayoutOrdersBanksMonitoring } from './BanksMonitoring';
import { PayoutOrdersRangesMonitoring } from './RangesMonitoring';

export const TraderPayoutOrdersMonitoringPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common);

  const { fiatCurrenciesOptions, getFiatCurrencyCode } = useCurrencies();
  const { paymentTypes } = useUserContext();

  const queryResult = usePartialQuery(
    QueryKey.PayoutOrdersMonitoring,
    ordersMonitoringApi.getPayoutAsTrader,
  );

  const availablePaymentTypes = useMemo(
    () =>
      reduce(
        queryResult.data?.availablePaymentTypes,
        (acc, paymentTypeCode) => {
          const paymentType = find(paymentTypes, { code: paymentTypeCode });
          if (paymentType) {
            acc.push(paymentType);
          }
          return acc;
        },
        [] as PaymentType[],
      ),
    [paymentTypes, queryResult.data?.availablePaymentTypes],
  );

  const availableColumns = useMemo(
    () =>
      map(availablePaymentTypes, (paymentType) => ({
        value: paymentType.code,
        label: orderMonitoringUtils.getPaymentTypeHeader(
          paymentType.code as DefaultPaymentTypeCode,
        ),
      })),
    [availablePaymentTypes],
  );

  const filtersDefinitions: FilterDefinition<PayoutOrdersMonitoringFilters>[] =
    useMemo(
      () => [
        {
          label: t('filters.fiat_currency'),
          name: 'fiatCurrencyId',
          type: FilterDefinitionType.Select,
          options: fiatCurrenciesOptions,
          getDisplayName: getFiatCurrencyCode,
        },
      ],
      [t, fiatCurrenciesOptions, getFiatCurrencyCode],
    );

  return (
    <Fragment>
      <PageHeader title={t('features.orders_monitoring.title')} />
      <div className="tw-flex tw-items-center tw-justify-end">
        <div className="tw-flex-1">
          <QueryFilters filtersDefinitions={filtersDefinitions} />
        </div>
        <QueryColumns
          title={t('features.orders_monitoring.columns_toggle_title')}
          availableColumns={availableColumns}
        />
      </div>
      <DataWrapper queryResult={queryResult}>
        <Fragment>
          <PayoutOrdersBanksMonitoring
            monitoringData={queryResult?.data?.bankMonitoringData}
            availablePaymentTypes={queryResult?.data?.availablePaymentTypes}
            className="tw-mb-8"
          />
          <PayoutOrdersRangesMonitoring
            monitoringData={queryResult?.data?.amountMonitoringData}
            availablePaymentTypes={queryResult?.data?.availablePaymentTypes}
          />
        </Fragment>
      </DataWrapper>
    </Fragment>
  );
};
